import {
  MinimalDeviceCategoryEnum,
  MinimalDeviceImportanceEnum,
  MinimalDeviceSourcesEnum,
} from '@rtkwlf/asset-data-service';
import type { Dayjs } from 'dayjs';
import type { DecodedValueMap } from 'use-query-params';
import {
  ArrayParam,
  createEnumArrayParam,
  DelimitedNumericArrayParam,
  StringParam,
} from 'use-query-params';

import {
  GridDateOperator,
  GridNumericOperator,
  GridSelectOperator,
  GridStringOperator,
} from '@packages/fenrir/components/datagrid';
import { DayJsParam } from '@packages/utils/dayjs.utils';

import { ASSET_COLUMNS_DEFS } from '../../../../components/assets-table/asset-table.constants';
import {
  getStringArray,
  getString,
  getDateArray,
} from '../../../../filter-management/filter-fixtures';

export type FilterMapItem = {
  column: string;
  param: FilterParamKey | FilterParamKey[];
  operator: string;
  getValue: (
    value: string | string[],
  ) =>
    | string
    | string[]
    | (undefined | number)[]
    | number
    | Dayjs
    | Dayjs[]
    | undefined;
};

/**
 * Represents a mapping between the data grid column filters and its corresponding configuration in ADS API.
 *
 * @property column - The name of the column used inside data grid filters.
 * @property param - The key name required by the API for filtering.
 * @property operator - The operator used to hard match the operator selected by the user to avoid mismatch with the API requirements.
 * @property getValue - The type of fixture to be used while formatting the data retrieved from the filter modal (data grid format).
 */
export const filterMap: FilterMapItem[] = [
  {
    column: ASSET_COLUMNS_DEFS.NAME,
    param: 'name',
    operator: GridStringOperator.Contains,
    getValue: getString,
  },
  {
    column: ASSET_COLUMNS_DEFS.CATEGORY,
    param: 'categories',
    operator: GridSelectOperator.isAnyOf,
    getValue: getStringArray,
  },
  {
    column: ASSET_COLUMNS_DEFS.SOURCES,
    param: 'sources',
    operator: GridSelectOperator.isAnyOf,
    getValue: getStringArray,
  },
  {
    column: ASSET_COLUMNS_DEFS.LAST_SEEN_TIME,
    param: ['lastSeenFrom', 'lastSeenTo'],
    operator: GridDateOperator.isBetween,
    getValue: getDateArray,
  },
  {
    column: ASSET_COLUMNS_DEFS.LAST_SUCCESSFUL_SCANNED,
    param: ['lastScanFrom', 'lastScanTo'],
    operator: GridDateOperator.isBetween,
    getValue: getDateArray,
  },
  {
    column: ASSET_COLUMNS_DEFS.IMPORTANCE,
    param: 'importances',
    operator: GridSelectOperator.isAnyOf,
    getValue: getStringArray,
  },
  {
    column: ASSET_COLUMNS_DEFS.TAGS,
    param: 'tags',
    operator: GridSelectOperator.isAnyOf,
    getValue: getStringArray,
  },
  {
    column: ASSET_COLUMNS_DEFS.IP_ADDRESS,
    param: 'ipAddress',
    operator: GridStringOperator.Contains,
    getValue: getString,
  },
  {
    column: ASSET_COLUMNS_DEFS.MAC,
    param: 'mac',
    operator: GridStringOperator.Contains,
    getValue: getString,
  },
  {
    column: ASSET_COLUMNS_DEFS.HOSTNAME,
    param: 'hostname',
    operator: GridStringOperator.Contains,
    getValue: getString,
  },
  {
    column: ASSET_COLUMNS_DEFS.OPERATING_SYSTEM,
    param: 'os',
    operator: GridStringOperator.Contains,
    getValue: getString,
  },
  {
    column: ASSET_COLUMNS_DEFS.RISK_SCORE,
    param: 'riskScore',
    operator: GridNumericOperator.isBetween,
    getValue: getStringArray,
  },
];

const CategoriesParam = createEnumArrayParam(
  Object.values(MinimalDeviceCategoryEnum),
);

const ImportancesParam = createEnumArrayParam(
  Object.values(MinimalDeviceImportanceEnum),
);

const SourcesParam = createEnumArrayParam(
  Object.values(MinimalDeviceSourcesEnum),
);

// Query Params mappings
export const queryParamsMap = {
  categories: CategoriesParam,
  importances: ImportancesParam,
  lastScanFrom: DayJsParam,
  lastScanTo: DayJsParam,
  lastSeenFrom: DayJsParam,
  lastSeenTo: DayJsParam,
  riskScore: DelimitedNumericArrayParam,
  sources: SourcesParam,
  tags: ArrayParam,
  name: StringParam,
  hostname: StringParam,
  os: StringParam,
  mac: StringParam,
  ipAddress: StringParam,
};

export type FilterQueryParamType = DecodedValueMap<typeof queryParamsMap>;
export type FilterParamKey = keyof typeof queryParamsMap;
